/**
 * Plan-summary fields filled from the PLATFORM SETUP of an already-launched
 * campaign.
 *
 * The review pane's tiles are plan-shaped: impression goal, plan budget, flight.
 * A campaign that was imported (or launched long ago) has none of those in its
 * payload — the Google Ads importer's json_response frequently carries no
 * campaign_budget_details and no start/end date at all — so the tiles read '—'
 * on a live campaign whose real budget and flight the Campaign Grid already
 * pulls every 4h into campaign_grid_metrics (budget = the platform's DAILY
 * amount, plus flight_start_date / flight_end_date and the account currency).
 *
 * This module merges that cached setup into a COPY of the summary, for display
 * only. It deliberately does not touch `launcherResult`: the plan is editable
 * and gets saved back to GCS, and writing platform-derived values into it would
 * persist numbers the user never entered as though they were part of the plan.
 *
 * Every derived figure is labelled for what it is — a stated total, an estimate
 * from a bounded flight, or Google's monthly maximum — because a budget number
 * whose basis is unclear is worse than a dash.
 */

// Google Ads returns this end date for a campaign with NO end date set. Treated
// as open-ended: multiplying a daily budget across ~12 years of "flight" would
// produce a headline number off by three orders of magnitude.
export const GADS_OPEN_ENDED_END_DATE = '2037-12-30';

// Google's documented monthly charging limit for a daily-budgeted campaign is
// the daily budget × 30.4 (their average days-per-month). It is the only total
// an open-ended daily budget actually has.
export const MONTHLY_BUDGET_DAYS = 30.4;

const DAY_MS = 24 * 60 * 60 * 1000;

/** ISO-ish date → 'YYYY-MM-DD', or null when unusable. */
export function ymd(value) {
  const s = value == null ? '' : String(value).trim();
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  // Google Ads also returns YYYYMMDD in some report shapes.
  const bare = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  return bare ? `${bare[1]}-${bare[2]}-${bare[3]}` : null;
}

/** Inclusive day count for a bounded flight; null when either end is unusable. */
export function flightDayCount(start, end) {
  const a = ymd(start);
  const b = ymd(end);
  if (!a || !b) return null;
  const ms = Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`);
  if (!Number.isFinite(ms) || ms < 0) return null;
  return Math.round(ms / DAY_MS) + 1;
}

/**
 * The cached grid row (GET /api/v2/campaign-grid/:id/delivered-metrics) reduced
 * to the setup facts the tiles can use. Returns null when the row carries none
 * of them — a campaign the grid has never pulled must not gain invented fields.
 */
export function normalizePlatformSetup(row) {
  if (!row || typeof row !== 'object') return null;
  const dailyRaw = row.budget;
  const daily = dailyRaw == null || dailyRaw === '' ? null : Number(dailyRaw);
  const dailyBudget = Number.isFinite(daily) && daily > 0 ? daily : null;
  const flightStart = ymd(row.flight_start_date);
  const rawEnd = ymd(row.flight_end_date);
  const openEnded = !rawEnd || rawEnd === GADS_OPEN_ENDED_END_DATE;
  const currencyCode = row.currency_code || null;
  if (dailyBudget == null && !flightStart && openEnded) return null;
  return {
    dailyBudget,
    currencyCode,
    flightStart,
    // The sentinel is not a date a reviewer should ever read.
    flightEnd: openEnded ? null : rawEnd,
    openEnded,
  };
}

/**
 * Summary + platform setup → summary. Only fills what the plan does not state;
 * a plan that carries its own budget or flight always wins.
 *
 * Added fields, all optional and all ignored by hosts that don't render them:
 *   dailyBudget, currencyCode         the platform's own figures
 *   flightStart / flightEnd           when the plan had no flight
 *   flightOpenEnded                   true when the campaign has no end date
 *   estimatedBudget + …Basis          daily × bounded flight days
 *   monthlyBudget + …Basis            daily × 30.4, for an open-ended flight
 *   platformSetupFields               which keys came from the platform
 */
export function mergePlatformSetup(summary, setupRow) {
  const setup = normalizePlatformSetup(setupRow);
  if (!setup) return summary;
  const s = { ...(summary || {}) };
  const filled = [];

  if (!s.currencyCode && setup.currencyCode) s.currencyCode = setup.currencyCode;

  // Flight: the plan's own dates win; otherwise the platform's.
  if (!s.flightStart && setup.flightStart) {
    s.flightStart = setup.flightStart;
    filled.push('flightStart');
  }
  if (!s.flightEnd && setup.flightEnd) {
    s.flightEnd = setup.flightEnd;
    filled.push('flightEnd');
  }
  if (!s.flightEnd && setup.openEnded) s.flightOpenEnded = true;

  if (s.dailyBudget == null && setup.dailyBudget != null) {
    s.dailyBudget = setup.dailyBudget;
    filled.push('dailyBudget');
  }

  // A budget the plan already states (total, or an estimate the plan's own
  // builder derived) is never overwritten.
  const hasPlanBudget = s.totalBudget != null || s.estimatedBudget != null;
  if (!hasPlanBudget && setup.dailyBudget != null) {
    const days = flightDayCount(s.flightStart, s.flightEnd);
    if (days != null) {
      s.estimatedBudget = setup.dailyBudget * days;
      s.estimatedBudgetBasis = `Est. from the platform's daily budget × ${days} flight day${days === 1 ? '' : 's'}`;
      filled.push('estimatedBudget');
    } else {
      s.monthlyBudget = setup.dailyBudget * MONTHLY_BUDGET_DAYS;
      s.monthlyBudgetBasis = `Daily budget × ${MONTHLY_BUDGET_DAYS} — Google's monthly maximum`;
      filled.push('monthlyBudget');
    }
  }

  if (filled.length) s.platformSetupFields = filled;
  return s;
}

/**
 * The budget tile's value / label / sub, so the three cases stay labelled: a
 * stated total, an estimate over a bounded flight, or the monthly maximum of an
 * open-ended daily budget.
 *
 * @param {object|null} s      merged summary
 * @param {function} money     (amount, currencyCode) => string
 */
export function budgetTile(s, money) {
  const code = s?.currencyCode || null;
  const flight = s?.flightStart
    ? `${s.flightStart} → ${s.flightEnd || 'no end date'}`
    : null;

  if (s?.totalBudget != null) {
    return {
      value: money(s.totalBudget, code),
      label: 'Total budget',
      sub: flight || 'Flight TBC',
    };
  }
  if (s?.estimatedBudget != null) {
    return {
      value: `~${money(s.estimatedBudget, code)}`,
      label: 'Est. total budget',
      sub: s.estimatedBudgetBasis || (flight ? `Est. over ${flight}` : 'Est. from CPM target'),
    };
  }
  if (s?.monthlyBudget != null) {
    return {
      value: `~${money(s.monthlyBudget, code)}`,
      label: 'Monthly max',
      sub: s.monthlyBudgetBasis || 'Open-ended daily budget',
    };
  }
  return { value: '—', label: 'Total budget', sub: flight || 'Flight TBC' };
}

const platformSetupSummary = {
  mergePlatformSetup, normalizePlatformSetup, budgetTile, flightDayCount, ymd,
};
export default platformSetupSummary;
